import Modal from "@/components/base/Modal";
import Badge from "@/components/base/Badge";
import { formatDate } from "@/utils/format";
import {
  DOCUMENT_TYPE_ICONS,
  DOCUMENT_TYPE_LABELS,
  formatFileSize,
  isImageDocument,
  isPdfDocument,
  type DocumentItem,
} from "../types";

interface DocumentPreviewModalProps {
  document: DocumentItem | null;
  onClose: () => void;
}

export default function DocumentPreviewModal({ document, onClose }: DocumentPreviewModalProps) {
  if (!document) return null;

  return (
    <Modal
      open={Boolean(document)}
      onClose={onClose}
      size="lg"
      title={document.nom}
      description={`${DOCUMENT_TYPE_LABELS[document.type]} · ${formatDate(document.date)} · ${formatFileSize(
        document.fichierTaille
      )}`}
    >
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-center overflow-hidden rounded-lg border border-background-200 bg-background-100">
          {isImageDocument(document) ? (
            <img
              src={document.fichierUrl}
              alt={document.nom}
              className="max-h-[60vh] w-full object-contain"
            />
          ) : isPdfDocument(document) ? (
            <iframe
              src={document.fichierUrl}
              title={document.nom}
              className="h-[60vh] w-full"
            />
          ) : (
            <div className="flex flex-col items-center justify-center gap-3 px-6 py-12 text-center">
              <span className="flex h-16 w-16 items-center justify-center rounded-lg bg-background-50 text-accent-700">
                <i className={`${DOCUMENT_TYPE_ICONS[document.type]} text-3xl`} aria-hidden="true" />
              </span>
              <p className="text-sm text-foreground-600">
                L'aperçu n'est pas disponible pour ce type de fichier. Vous pouvez le télécharger pour le consulter.
              </p>
            </div>
          )}
        </div>

        <div className="flex flex-col gap-2 rounded-lg border border-background-200 bg-background-50 p-3.5">
          <p className="flex items-center gap-2 text-xs text-foreground-600">
            <i className="ri-file-line shrink-0 text-sm text-foreground-400" aria-hidden="true" />
            <span className="truncate">{document.fichierNom || document.nom}</span>
          </p>
          <Badge tone="neutral" icon="ri-information-line">
            {DOCUMENT_TYPE_LABELS[document.type]}
          </Badge>
        </div>

        <div className="flex flex-col-reverse gap-2 border-t border-background-200 pt-4 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center justify-center whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-5 py-2.5 text-sm font-label font-medium text-foreground-900 transition-colors hover:bg-background-100 cursor-pointer"
          >
            Fermer
          </button>
          <a
            href={document.fichierUrl || undefined}
            download={document.fichierNom || document.nom}
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
          >
            <i className="ri-download-2-line text-base" aria-hidden="true" />
            Télécharger
          </a>
        </div>
      </div>
    </Modal>
  );
}